import { useEffect, useState } from 'react';
import { Snackbar, Alert, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';

/**
 * Safari on iOS doesn't fire `beforeinstallprompt`, so InstallPrompt.tsx
 * never surfaces there. This component shows a one-time hint with the
 * "Tap Share → Add to Home Screen" instruction. Dismissed permanently
 * after one tap (per device, via localStorage).
 *
 * Heuristics for showing:
 *   - iOS Safari (not Chrome on iOS, not other browsers)
 *   - Not already running as installed PWA (display-mode: standalone)
 *   - Not already dismissed
 */
const DISMISS_KEY = 'pwa.ios-hint.dismissed';

function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  if (!isIOS) return false;
  // Chrome / Firefox on iOS use CriOS / FxiOS in the UA. Only show on real Safari.
  if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false;
  return true;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari uses navigator.standalone; modern browsers use display-mode.
  return Boolean(
    (window.navigator as any).standalone ||
    window.matchMedia?.('(display-mode: standalone)').matches
  );
}

export default function IOSInstallHint() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isIOSSafari() || isStandalone()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch {}
    // Wait a few seconds before showing so the page has settled.
    const t = window.setTimeout(() => setOpen(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
  };

  if (!open) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: 2 }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <IconButton color="inherit" size="small" onClick={handleDismiss} aria-label="Dismiss">
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Install MoreMe AI: tap
          </Typography>
          <IosShareIcon fontSize="small" />
          <Typography variant="body2">
            then "Add to Home Screen".
          </Typography>
        </Box>
      </Alert>
    </Snackbar>
  );
}
